/**
 * useI18n.js — lightweight module-level i18n store (no provider).
 *
 *   import { t, useI18n, setLang } from '../hooks/useI18n';
 *   const { lang, t } = useI18n();
 *   <span>{t('nav.density_radar')}</span>
 *
 * The user's choice is persisted in localStorage (`qr_lang`, default 'en').
 * The site is English-first; Russian is opt-in via the language switcher.
 * Missing keys fall back to the key itself so untranslated strings are obvious.
 */
import { getLang as _get, setLang as _set, useLang, tr } from '@quantum/ui';

export { tr };

const DICT = {
  ru: {
    'nav.density_radar':  'Radar',
    'nav.quant_screener': 'Screener',
    'nav.situations':     'Ситуации',
    'nav.onchain':        'On-chain',
    'nav.ideas':          'Ideas',
    'nav.grid_watchlist': 'Watchlist',
    'nav.bots':           'Боты',
    'btn.sign_in':        'Войти',
    'btn.live':           'LIVE',
    'lang.label':         'Язык',
    'lang.ru':            'Рус',
    'lang.en':            'Eng',
    'tab.market':         'Рынок',
    'tab.density':        'Заявки',
    'tab.impulse':        'Импульс',
    'tab.setup':          'Сетапы',
    'positions.open':     'Открытые позиции',
    'positions.empty':    'Нет открытых позиций. Используй LONG / SHORT в рейле для входа.',
    'btn.journal':        'ЖУРНАЛ',
    'btn.lock':           'ЛОК',
    'btn.locked':         'ЗАБЛОК.',
    'btn.close_mkt':      'CLOSE MKT',
    'btn.close_lmt':      'CLOSE LMT',
    'col.size':           'РАЗМЕР',
    'col.entry':          'ВХОД',
    'col.mark':           'МАРК',
    'col.pnl':            'PnL',
    'col.age':            'ВОЗРАСТ',
    'wall.stop':          'СТОП',
    'wall.take':          'ТЕЙК',
    'wall.liq':           'ЛИКВ',
    'impulse.move_min':   'ДВИЖЕНИЕ ≥',
    'impulse.sharp':      'РЕЗКИЕ ДВИЖЕНИЯ',
    'impulse.waiting':    'Ожидание импульсов…',
    'walls.live':         'Live Walls',
    'walls.title':        'Orders',
    'walls.all':          'ВСЕ',
    'walls.bids':         'БИДЫ',
    'walls.asks':         'АСКИ',
    'walls.min_size':     'МИН. РАЗМЕР',
    'walls.max_dist':     'МАКС. ДИСТАНЦИЯ',
    'walls.zscore':       'Z-SCORE ≥',
    'walls.sort':         'СОРТ',
    'liq.events':         'СОБЫТИЯ ЛИКВИДНОСТИ',
    'liq.waiting':        'ожидание потока ликвидности…',
    'screener.search':    'Поиск монеты…',
    'screener.banned':    'ЗАБАНЕНО',
    'screener.clear_all': 'УБРАТЬ ВСЕ',
    'screener.awaiting':  'ОЖИДАНИЕ ДАННЫХ',
    'tag.tags':           'ТЕГИ',
    'tag.add':            '+ ДОБАВИТЬ',
    'tag.none':           'Нет — нажми +',
    'tag.from_journal':   'ИЗ ЖУРНАЛА',
    // Risk Calculator
    'calc.title':         'КАЛЬКУЛЯТОР РИСКА',
    'calc.insuff_margin': '⚠ НЕДОСТАТОЧНО МАРЖИ',
    'calc.account_risk':  'СЧЁТ И РИСК',
    'calc.deposit':       'Депозит (USD)',
    'calc.risk_pct':      'Риск (%)',
    'calc.leverage':      'Плечо',
    'calc.entry_params':  'ПАРАМЕТРЫ ВХОДА',
    'calc.entry':         'Вход',
    'calc.stop_loss':     'Стоп-лосс',
    'calc.results':       'РЕЗУЛЬТАТЫ',
    'calc.risk_usd':      'РИСК $',
    'calc.actual_pct':    'ФАКТ %',
    'calc.size_usd':      'ОБЪЁМ USD',
    'calc.req_margin':    'МАРЖА',
    'calc.qty':           'КОЛ-ВО',
    'calc.take_profits':  'ТЕЙК-ПРОФИТЫ',
    'calc.add_tp':        '+ ТЕЙК',
    'calc.auto':          'АВТО',
    'calc.expected_pnl':  'ОЖИД. PNL',
    'calc.rr_ratio':      'R:R',
    'calc.confirm_trade': 'Подтвердить трейд',
    'calc.place_position':'РАЗМЕСТИТЬ НА ГРАФИКЕ',
    'calc.connect_binance':'ПОДКЛЮЧИТЬ BINANCE',
    // Screener filters / tooltips
    'sc.min_volume':      'МИН. ОБЪЁМ',
    'sc.funding':         'ФАНДИНГ',
    'sc.groups':          'ГРУППЫ:',
    'sc.all':             'ВСЕ',
    'sc.search':          'ПОИСК...',
    'sc.tt.cursor':       'Курсор (Esc)',
    'sc.tt.trend':        'Трендовая линия',
    'sc.tt.horizontal':   'Горизонтальная линия',
    'sc.tt.alert':        'Звуковой алерт (клик по графику)',
    'sc.tt.magnet':       'Магнит',
    'sc.tt.clear':        'Очистить рисунки',
    'sc.tt.long':         'Калькулятор LONG',
    'sc.tt.short':        'Калькулятор SHORT',
    'sc.tt.positions':    'Открытые позиции',
    'sc.tt.hide':         'Скрыть монету из списка',
    // Positions panel
    'pos.place_order':    'РАЗМЕСТИТЬ ОРДЕР',
    'pos.save':           'СОХРАНИТЬ',
    'pos.cancel':         'ОТМЕНА',
    'pos.journal':        'ЖУРНАЛ',
    'pos.tt.expand':      'Развернуть открытые позиции',
    'pos.tt.journal':     'Открыть журнал сделок',
    'pos.tt.close':       'Закрыть',
    'pos.tag_ph':         'название тега…',
    'pos.note_ph':        'Почему вошёл в сделку? Сетап, сигналы, план…',
    // ThreeMap / Orders panel
    'tm.size':            'РАЗМЕР',
    'tm.dist':            'ДИСТ',
    'tm.zscore':          'Z-SCORE',
    'tm.demo':            'ДЕМО-СЕТЬ',
    'tm.radar_view':      'ВИД РАДАРА',
    'tm.reset_view':      'СБРОС ВИДА',
    'tm.min_size':        'МИН. РАЗМЕР',
    'tm.max_dist':        'МАКС. ДИСТАНЦИЯ',
    'tm.sort':            'СОРТ',
    // ImpulseFeed / Alerts / Settings
    'imp.open_chart':     'Открыть график',
    'al.now':             'СЕЙЧАС',
    'al.price_alerts':    'Ценовые алерты',
    'set.title':          'Настройки',
    'set.done':           'ГОТОВО',
    // Trade Journal
    'tj.title':           'Журнал сделок',
    'tj.loaded_bnc':      'Загружено с Binance',
    'tj.tags':            'ТЕГИ',
    'tj.notes':           'ЗАМЕТКИ',
    'tj.note_ph':         'Сетап, сигналы, план, причины входа…',
    'tj.tag_stats':       'Статистика по тегам',
    'tj.col_tag':         'ТЕГ',
    'tj.col_trades':      'СДЕЛОК',
    'tj.col_dist':        'РАСПРЕДЕЛЕНИЕ',
    'tj.no_tag_trades':   'Нет закрытых сделок с тегами.',
    'tj.select_day':      'ВЫБЕРИТЕ ДЕНЬ',
    'tj.select_trade':    'ВЫБЕРИТЕ СДЕЛКУ',
    'tj.search_ph':       'Поиск монеты…',
    'tj.clear_cache_tt':  'Очистить кеш и перезагрузить всю историю (только если данные неверны)',
    // IntelFeed / Situations
    'if.dist_to_entry':   'ДИСТ. ДО ВХОДА',
    'if.indicators':      'ИНДИКАТОРЫ',
    'if.clear':           'Очистить',
    'if.publish_sit':     'Опубликовать ситуацию',
    'if.cancel':          'ОТМЕНА',
    'if.publish_btn':     'ОПУБЛИКОВАТЬ',
    'if.private':         'ПРИВАТНО',
    'if.search_ph':       'Поиск…',
    'if.top24h':          'ТОП-10 за 24ч',
    'if.side':            'СТОРОНА',
    'if.entry':           'ВХОД',
    'if.take_profit':     'ТЕЙК-ПРОФИТ',
    'if.stop_loss':       'СТОП-ЛОСС',
    'if.title':           'Заголовок',
    'if.title_ph':        'Тезис одной строкой…',
    'if.summary':         'Описание',
    'if.summary_ph':      'Почему этот сетап? Контекст, триггеры, инвалидация…',
    'if.tags':            'Теги',
    'if.ai_verdict':      'ВЕРДИКТ AI',
    'if.author_chart':    'ГРАФИК АВТОРА',
    'if.distance':        'ДИСТАНЦИЯ',
    'if.author_thesis':   'Тезис автора',
    'if.trade_params':    'Параметры сделки',
    'if.author':          'Автор',
    'if.ai_unavailable':  'AI-анализ недоступен',
    'if.retry_ai':        'ПОВТОРИТЬ АНАЛИЗ',
    'if.comment_ph':      'Добавь мнение…',
    'if.post':            'ОТПРАВИТЬ',
    'if.your_verdict':    'ТВОЙ ВЕРДИКТ',
    'if.delete_sit':      'Удалить ситуацию',
    'if.min_ai':          'МИН. AI',
    // Ideas
    'id.submit':          'Предложить идею',
    'id.title':           'Заголовок',
    'id.title_ph':        'Одно ясное предложение — что нам построить?',
    'id.details':         'Детали',
    'id.details_ph':      'Почему это важно, как бы ты использовал, ссылки…',
    'id.tags':            'Теги',
    'id.cancel':          'ОТМЕНА',
    'id.post':            'ОПУБЛИКОВАТЬ',
    'id.delete':          'Удалить',
    'id.no_comments':     'Нет комментариев',
    'id.comment_ph':      'Добавить комментарий…',
    'id.send':            'Отправить',
    'id.full_comment':    'Полный комментарий',
    // Billing
    'bill.choose_sub':    'Выбери подписку',
    'bill.select':        'ВЫБРАТЬ',
    'bill.to_pay':        'К ОПЛАТЕ',
    'bill.choose_method': 'ВЫБЕРИ СПОСОБ ОПЛАТЫ',
    'bill.confirmed':     'Оплата подтверждена!',
    'bill.activated':     'Подписка активирована. Перезагрузка…',
    'bill.choose_plan':   'ВЫБОР ТАРИФА',
    'bill.payment':       'ОПЛАТА',
    // Profile
    'prof.save':          'СОХРАНИТЬ',
    'prof.score':         'РЕЙТИНГ',
    'prof.days_left':     'ОСТАЛОСЬ ДНЕЙ',
    'prof.api_key':       'API КЛЮЧ',
    'prof.api_key_ph':    'Введите Binance API key…',
    'prof.api_secret':    'API SECRET',
    'prof.api_secret_ph': 'Введите Binance API secret…',
    'prof.sign_out':      'ВЫЙТИ',
    'prof.done':          'ГОТОВО',
    'prof.tt.avatar':     'Загрузить аватар',
    // Auth
    'auth.welcome':       'Добро пожаловать в Quantum',
    'auth.continue_tg':   'Войти через Telegram',
    'auth.continue_gm':   'Войти через Gmail',
    'auth.change':        'СМЕНИТЬ',
    // Indicator context menu
    'ctx.what':           'Что это такое?',
    'ctx.how':            'Как использовать:',
    'ctx.settings':       'Настройки',
    'ctx.reset':          'Сбросить настройки',
    'ctx.hide':           'Скрыть индикатор',
    // Accumulation filter chips
    'acc.all':            'ВСЕ',
    'acc.inside':         'В ЗОНЕ',
    'acc.breakout':       'ПРОБОЙ↑',
    'acc.dist':           'ДИСТ↓',
    'acc.tt.all':         'Показать все монеты',
    'acc.tt.inside':      'Цена сейчас внутри зоны накопления (D1 бокс)',
    'acc.tt.breakout':    'Только что вышла из зоны вверх',
    'acc.tt.dist':        'Пробой зоны вниз — кандидаты в шорт',
    // Situations filters
    'flt.all_filters':    'ВСЕ ФИЛЬТРЫ',
    'flt.reset':          'СБРОСИТЬ',
    'flt.signal_type':    'ТИП СИГНАЛА',
    'flt.all':            'ВСЕ',
    'flt.auto':           '🤖 АВТО',
    'flt.mine':           'МОИ',
    'flt.others':         'ЧУЖИЕ',
    'flt.direction':      'НАПРАВЛЕНИЕ',
    'flt.ai_min':         'AI МИНИМУМ',
    'flt.rating_min':     'РЕЙТИНГ ▲ МИНИМУМ',
    'flt.apply':          'ПРИМЕНИТЬ',
    'flt.reset_all':      'СБРОСИТЬ ВСЁ',
    // Profile: API keys + language
    'prof.keys_set':      'API ключи настроены',
    'prof.change_keys':   'ИЗМЕНИТЬ',
    'prof.save_keys':     'СОХРАНИТЬ',
    'prof.cancel':        'ОТМЕНА',
    'prof.keys_hint_b':   'без права вывода средств',
    'prof.keys_hint_1':   'Создавайте API-ключи ',
    'prof.keys_hint_2':   ' (только Reading + Futures). Ключи хранятся локально в вашем браузере и не передаются на наши серверы.',
    'prof.lang':          'Язык интерфейса',
    // Data status banner
    'banner.server':      'Binance временно ограничил наш сервер — загрузка рыночных данных приостановлена для всех{s}. Восстановится автоматически.',
    'banner.user':        'Слишком много запросов с вашего устройства — данные приостановлены{s}. Подождите немного.',
    'banner.stale':       'Поток данных прерван — цены не обновляются с {t}. Не принимайте решений по застывшим значениям; переподключаемся…',
    // Journal
    'tj.csv_tt':          'Экспортировать текущую выборку сделок в CSV',
    // Chart liq tooltip
    'liq.short_stops':    '▲ стопы шортов',
    'liq.long_stops':     '▼ стопы лонгов',
    'liq.strength':       'сила',
    'liq.est':            'ликвидации ~',
    'liq.real':           'реальные',
    // Product tour
    'tour.next':          'Далее',
    'tour.prev':          'Назад',
    'tour.done':          'Готово',
    'tour.launch':        'Запустить тур по интерфейсу',
    'tour.onboard':       '👋 Новичок? Нажми — тур по странице',
    // Landing
    'lp.slogan':          'Торгуй вместе с остальными',
    'lp.sub':             'Реальные торговые индикаторы, основанные на статистике, с автоматическими виртуальными сделками от системы и пользователей.',
    'lp.cta':             'Войти / Авторизоваться',
    'lp.f1t':             'Индикаторы на статистике',
    'lp.f1d':             'Реальные торговые сигналы, проверенные на исторических данных — уровни, ликвидность и дивергенции без гадания.',
    'lp.f2t':             'Автоматические сделки',
    'lp.f2d':             'Система и пользователи генерируют виртуальные позиции в реальном времени — смотри, как отрабатывает каждый сетап.',
    'lp.f3t':             'Совместная торговля',
    'lp.f3d':             'Видишь, что торгуют другие, и торгуешь вместе с сообществом — общие сетапы, статистика и результаты.',
    // ── Literal-key translations (ключ = английская строка из компонента) ──
    // On-chain
    'Top movers':          'Лидеры потоков',
    'Whale bias':          'Позиции китов',
    'Largest whale positions': 'Крупнейшие позиции китов',
    'Unlock calendar':     'Календарь анлоков',
    'NO DATA':             'НЕТ ДАННЫХ',
    'NO UNLOCKS IN THE SELECTED WINDOW': 'НЕТ АНЛОКОВ В ВЫБРАННОМ ОКНЕ',
    'NO WHALE POSITIONS ≥ $100K ON THIS COIN': 'НЕТ ПОЗИЦИЙ КИТОВ ≥ $100K ПО ЭТОЙ МОНЕТЕ',
    'SUPPLY DATA UNAVAILABLE': 'ДАННЫЕ ПО САППЛАЮ НЕДОСТУПНЫ',
    'requires COINMARKETCAP_KEY on the server': 'нужен COINMARKETCAP_KEY на сервере',
    'search coin…':        'поиск монеты…',
    'derivatives flows · whale positioning · supply & unlocks — free': 'потоки деривативов · позиции китов · сапплай и анлоки — бесплатно',
    'COMPUTING SCORE…':    'СЧИТАЕМ СКОР…',
    'SCORE UNAVAILABLE':   'СКОР НЕДОСТУПЕН',
    'strongest aggressive buying (24h)':  'сильнейшие агрессивные покупки (24ч)',
    'strongest aggressive selling (24h)': 'сильнейшие агрессивные продажи (24ч)',
    'fastest open-interest build-up (24h)': 'быстрейший набор открытого интереса (24ч)',
    'fastest open-interest unwind (24h)':   'быстрейший слив открытого интереса (24ч)',
    'taker buy − sell (USD)': 'тейкер-покупки − продажи (USD)',
    'open interest':       'открытый интерес',
    'COIN':                'МОНЕТА',
    'TRADER':              'ТРЕЙДЕР',
    'SIDE':                'СТОРОНА',
    'SIZE':                'РАЗМЕР',
    'ENTRY':               'ВХОД',
    'MARKET CAP':          'КАПИТАЛИЗАЦИЯ',
    'VOL 24H':             'ОБЪЁМ 24Ч',
    'CIRCULATING':         'В ОБРАЩЕНИИ',
    'UNLOCK FEED TEMPORARILY UNAVAILABLE — free sources unreachable, retrying every 10 min.': 'ЛЕНТА АНЛОКОВ ВРЕМЕННО НЕДОСТУПНА — бесплатные источники не отвечают, повтор каждые 10 мин.',
    'today':               'сегодня',
    // Common UI
    'Situations':          'Ситуации',
    'Ideas':               'Идеи',
    'Bugs':                'Баги',
    'Orders':              'Ордера',
    'Live Walls':          'Живые стены',
    'CREATE SITUATION':    'СОЗДАТЬ СИТУАЦИЮ',
    'situations · {n} after filter': 'ситуаций · {n} после фильтра',
    'Newest':              'Новые',
    'Top rated':           'Топ рейтинг',
    'AI score':            'AI-скор',
    'active':              'активно',
    'No trades yet':       'Пока нет сделок',
    'Your closed trades will appear here once you start trading.': 'Закрытые сделки появятся здесь, как только начнёшь торговать.',
    'Add your Binance API keys in Settings to sync your trade history.': 'Добавь API-ключи Binance в настройках, чтобы синхронизировать историю сделок.',
    'REALIZED':            'РЕАЛИЗОВАНО',
    'WINS':                'ПРИБЫЛЬНЫХ',
    'LOSSES':              'УБЫТОЧНЫХ',
    'Syncing…':            'Синхронизация…',
    'ideas · build the future': 'идей · строим будущее вместе',
    'bugs · help us squash them': 'багов · помоги их прихлопнуть',
    'Alerts':              'Алерты',
    'Unrealized PnL':      'Нереализ. PnL',
    'Action':              'Действие',
    'Add Charts':          'Добавить графики',
    'Configure grid':      'Настроить сетку',
    'CANCEL':              'ОТМЕНА',
    'BUILD GRID':          'СОБРАТЬ СЕТКУ',
    'BAN COIN':            'СКРЫТЬ МОНЕТУ',
    'SITUATION HINT':      'ПОДСКАЗКА СИТУАЦИИ',
    'LARGE LIMITS':        'КРУПНЫЕ ЛИМИТКИ',
    'Open the Radar tab':  'Открой вкладку Radar',
    'Favorite setups':     'Избранные сетапы',
  },
  en: {
    'nav.density_radar':  'Radar',
    'nav.quant_screener': 'Screener',
    'nav.situations':     'Situations',
    'nav.onchain':        'On-chain',
    'nav.ideas':          'Ideas',
    'nav.grid_watchlist': 'Watchlist',
    'nav.bots':           'Bots',
    'btn.sign_in':        'SIGN IN',
    'btn.live':           'LIVE',
    'lang.label':         'Language',
    'lang.ru':            'Rus',
    'lang.en':            'Eng',
    'tab.market':         'Market',
    'tab.density':        'Density',
    'tab.impulse':        'Impulse',
    'tab.setup':          'Setup',
    'positions.open':     'Open Positions',
    'positions.empty':    'No open positions. Use LONG / SHORT in the rail to enter.',
    'btn.journal':        'JOURNAL',
    'btn.lock':           'LOCK',
    'btn.locked':         'LOCKED',
    'btn.close_mkt':      'CLOSE MKT',
    'btn.close_lmt':      'CLOSE LMT',
    'col.size':           'SIZE',
    'col.entry':          'ENTRY',
    'col.mark':           'MARK',
    'col.pnl':            'PnL',
    'col.age':            'AGE',
    'wall.stop':          'STOP',
    'wall.take':          'TAKE',
    'wall.liq':           'LIQ',
    'impulse.move_min':   'MOVE ≥',
    'impulse.sharp':      'SHARP MOVES',
    'impulse.waiting':    'Waiting for impulses…',
    'walls.live':         'Live Walls',
    'walls.title':        'Orders',
    'walls.all':          'ALL',
    'walls.bids':         'BIDS',
    'walls.asks':         'ASKS',
    'walls.min_size':     'MIN SIZE',
    'walls.max_dist':     'MAX DISTANCE',
    'walls.zscore':       'Z-SCORE ≥',
    'walls.sort':         'SORT',
    'liq.events':         'LIQUIDITY EVENTS',
    'liq.waiting':        'awaiting liquidity stream…',
    'screener.search':    'Search symbol…',
    'screener.banned':    'BANNED',
    'screener.clear_all': 'CLEAR ALL',
    'screener.awaiting':  'AWAITING DATA',
    'tag.tags':           'TAGS',
    'tag.add':            '+ ADD',
    'tag.none':           'None — click +',
    'tag.from_journal':   'FROM JOURNAL',
    // Risk Calculator
    'calc.title':         'RISK CALCULATOR',
    'calc.insuff_margin': '⚠ INSUFFICIENT MARGIN',
    'calc.account_risk':  'ACCOUNT & RISK',
    'calc.deposit':       'Deposit (USD)',
    'calc.risk_pct':      'Risk (%)',
    'calc.leverage':      'Leverage',
    'calc.entry_params':  'ENTRY PARAMETERS',
    'calc.entry':         'Entry',
    'calc.stop_loss':     'Stop Loss',
    'calc.results':       'RESULTS',
    'calc.risk_usd':      'RISK $',
    'calc.actual_pct':    'ACTUAL %',
    'calc.size_usd':      'SIZE USD',
    'calc.req_margin':    'REQ MARGIN',
    'calc.qty':           'QTY',
    'calc.take_profits':  'TAKE PROFITS',
    'calc.add_tp':        '+ ADD TP',
    'calc.auto':          'AUTO',
    'calc.expected_pnl':  'EXPECTED PNL',
    'calc.rr_ratio':      'R:R RATIO',
    'calc.confirm_trade': 'Confirm trade',
    'calc.place_position':'PLACE POSITION ON CHART',
    'calc.connect_binance':'CONNECT BINANCE',
    // Screener filters / tooltips
    'sc.min_volume':      'MIN VOLUME',
    'sc.funding':         'FUNDING',
    'sc.groups':          'GROUPS:',
    'sc.all':             'ALL',
    'sc.search':          'SEARCH...',
    'sc.tt.cursor':       'Cursor Tool (Esc)',
    'sc.tt.trend':        'Trend Line',
    'sc.tt.horizontal':   'Horizontal Line',
    'sc.tt.alert':        'Place Sound Alert (Click on Active Chart)',
    'sc.tt.magnet':       'Magnet Mode',
    'sc.tt.clear':        'Clear Drawings',
    'sc.tt.long':         'LONG Position Calculator',
    'sc.tt.short':        'SHORT Position Calculator',
    'sc.tt.positions':    'Open positions',
    'sc.tt.hide':         'Hide this coin from the screener list',
    // Positions panel
    'pos.place_order':    'PLACE ORDER',
    'pos.save':           'SAVE',
    'pos.cancel':         'CANCEL',
    'pos.journal':        'JOURNAL',
    'pos.tt.expand':      'Expand open positions',
    'pos.tt.journal':     'Open trade journal',
    'pos.tt.close':       'Close',
    'pos.tag_ph':         'tag name…',
    'pos.note_ph':        'Why did you take this trade? Setup, signals, plan…',
    // ThreeMap / Orders panel
    'tm.size':            'SIZE',
    'tm.dist':            'DIST',
    'tm.zscore':          'Z-SCORE',
    'tm.demo':            'DEMO NETWORK',
    'tm.radar_view':      'RADAR VIEW',
    'tm.reset_view':      'RESET VIEW',
    'tm.min_size':        'MIN SIZE',
    'tm.max_dist':        'MAX DISTANCE',
    'tm.sort':            'SORT',
    // ImpulseFeed / Alerts / Settings
    'imp.open_chart':     'Click to open chart',
    'al.now':             'NOW',
    'al.price_alerts':    'Price Alerts',
    'set.title':          'Settings',
    'set.done':           'DONE',
    // Trade Journal
    'tj.title':           'Trade Journal',
    'tj.loaded_bnc':      'Loaded from Binance',
    'tj.tags':            'TAGS',
    'tj.notes':           'TRADE NOTES',
    'tj.note_ph':         'Setup, signals, plan, reasons to enter…',
    'tj.tag_stats':       'Tag statistics',
    'tj.col_tag':         'TAG',
    'tj.col_trades':      'TRADES',
    'tj.col_dist':        'DISTRIBUTION',
    'tj.no_tag_trades':   'No closed trades with tags.',
    'tj.select_day':      'SELECT A DAY',
    'tj.select_trade':    'SELECT A TRADE',
    'tj.search_ph':       'Search symbol…',
    'tj.clear_cache_tt':  'Clear cache and reload the entire history (use only if you see incorrect data)',
    // IntelFeed / Situations
    'if.dist_to_entry':   'DIST TO ENTRY',
    'if.indicators':      'INDICATORS',
    'if.clear':           'Clear',
    'if.publish_sit':     'Publish a market situation',
    'if.cancel':          'CANCEL',
    'if.publish_btn':     'PUBLISH SITUATION',
    'if.private':         'PRIVATE',
    'if.search_ph':       'Search…',
    'if.top24h':          'TOP-10 24h',
    'if.side':            'SIDE',
    'if.entry':           'ENTRY',
    'if.take_profit':     'TAKE PROFIT',
    'if.stop_loss':       'STOP LOSS',
    'if.title':           'Title',
    'if.title_ph':        'One-line thesis…',
    'if.summary':         'Summary',
    'if.summary_ph':      'Why this setup? Context, triggers, invalidation…',
    'if.tags':            'Tags',
    'if.ai_verdict':      'AI VERDICT',
    'if.author_chart':    "AUTHOR'S CHART",
    'if.distance':        'DISTANCE',
    'if.author_thesis':   "Author's thesis",
    'if.trade_params':    'Trade parameters',
    'if.author':          'Author',
    'if.ai_unavailable':  'AI analysis unavailable',
    'if.retry_ai':        'RETRY ANALYSIS',
    'if.comment_ph':      'Add your take…',
    'if.post':            'POST',
    'if.your_verdict':    'YOUR VERDICT',
    'if.delete_sit':      'Delete situation',
    'if.min_ai':          'MIN AI',
    // Ideas
    'id.submit':          'Submit an idea',
    'id.title':           'Title',
    'id.title_ph':        'One clear sentence — what should we build?',
    'id.details':         'Details',
    'id.details_ph':      "Why it matters, how you'd use it, any references…",
    'id.tags':            'Tags',
    'id.cancel':          'CANCEL',
    'id.post':            'POST IDEA',
    'id.delete':          'Delete',
    'id.no_comments':     'No comments',
    'id.comment_ph':      'Add a comment…',
    'id.send':            'Send',
    'id.full_comment':    'Full comment',
    // Billing
    'bill.choose_sub':    'Choose your subscription',
    'bill.select':        'SELECT',
    'bill.to_pay':        'TO PAY',
    'bill.choose_method': 'CHOOSE A PAYMENT METHOD',
    'bill.confirmed':     'Payment confirmed!',
    'bill.activated':     'Subscription activated. Reloading…',
    'bill.choose_plan':   'CHOOSE PLAN',
    'bill.payment':       'PAYMENT',
    // Profile
    'prof.save':          'SAVE',
    'prof.score':         'SCORE',
    'prof.days_left':     'DAYS LEFT',
    'prof.api_key':       'API KEY',
    'prof.api_key_ph':    'Enter Binance API key…',
    'prof.api_secret':    'API SECRET',
    'prof.api_secret_ph': 'Enter Binance API secret…',
    'prof.sign_out':      'SIGN OUT',
    'prof.done':          'DONE',
    'prof.tt.avatar':     'Upload avatar',
    // Auth
    'auth.welcome':       'Welcome to Quantum',
    'auth.continue_tg':   'Continue with Telegram',
    'auth.continue_gm':   'Continue with Gmail',
    'auth.change':        'CHANGE',
    // Indicator context menu
    'ctx.what':           'What is this?',
    'ctx.how':            'How to use:',
    'ctx.settings':       'Settings',
    'ctx.reset':          'Reset settings',
    'ctx.hide':           'Hide indicator',
    // Accumulation filter chips
    'acc.all':            'ALL',
    'acc.inside':         'IN ZONE',
    'acc.breakout':       'BREAK↑',
    'acc.dist':           'DIST↓',
    'acc.tt.all':         'Show all coins',
    'acc.tt.inside':      'Price is currently inside an accumulation zone (D1 box)',
    'acc.tt.breakout':    'Just broke out of the zone upward',
    'acc.tt.dist':        'Broke down out of the zone — short candidates',
    // Situations filters
    'flt.all_filters':    'ALL FILTERS',
    'flt.reset':          'RESET',
    'flt.signal_type':    'SIGNAL TYPE',
    'flt.all':            'ALL',
    'flt.auto':           '🤖 AUTO',
    'flt.mine':           'MINE',
    'flt.others':         'OTHERS',
    'flt.direction':      'DIRECTION',
    'flt.ai_min':         'AI MINIMUM',
    'flt.rating_min':     'RATING ▲ MINIMUM',
    'flt.apply':          'APPLY',
    'flt.reset_all':      'RESET ALL',
    // Profile: API keys + language
    'prof.keys_set':      'API keys configured',
    'prof.change_keys':   'CHANGE',
    'prof.save_keys':     'SAVE',
    'prof.cancel':        'CANCEL',
    'prof.keys_hint_b':   'without withdrawal permission',
    'prof.keys_hint_1':   'Create API keys ',
    'prof.keys_hint_2':   ' (Reading + Futures only). Keys are stored locally in your browser and never sent to our servers.',
    'prof.lang':          'Interface language',
    // Data status banner
    'banner.server':      'Binance temporarily rate-limited our server — market data loading is paused for everyone{s}. It will recover automatically.',
    'banner.user':        'Too many requests from your device — data is paused{s}. Please wait a moment.',
    'banner.stale':       'Data stream interrupted — prices have not updated since {t}. Do not act on frozen values; reconnecting…',
    // Journal
    'tj.csv_tt':          'Export the current trade selection to CSV',
    // Chart liq tooltip
    'liq.short_stops':    '▲ short stops',
    'liq.long_stops':     '▼ long stops',
    'liq.strength':       'strength',
    'liq.est':            'liquidations ~',
    'liq.real':           'real',
    // Product tour
    'tour.next':          'Next',
    'tour.prev':          'Back',
    'tour.done':          'Done',
    'tour.launch':        'Launch interface tour',
    'tour.onboard':       '👋 New here? Click for a page tour',
    // Landing
    'lp.slogan':          'Trade together with everyone else',
    'lp.sub':             'Real trading indicators built on statistics, with automatic virtual trades from the system and its users.',
    'lp.cta':             'Sign In / Get Access',
    'lp.f1t':             'Statistics-based indicators',
    'lp.f1d':             'Real trading signals verified on historical data — levels, liquidity and divergences without guesswork.',
    'lp.f2t':             'Automatic trades',
    'lp.f2d':             'The system and its users generate virtual positions in real time — watch how every setup plays out.',
    'lp.f3t':             'Community trading',
    'lp.f3d':             'See what others are trading and trade along with the community — shared setups, stats and results.',
  },
};

// Reload so strings read once via tr() (not subscribed) switch too.
export const setLang = (l) => { if (l === _get()) return; _set(l); location.reload(); };
export const getLang = _get;

export function t(key) {
  return DICT[_get()]?.[key] ?? tr(key);
}

export function useI18n() {
  const lang = useLang();
  return { lang, setLang, t: (k) => DICT[lang]?.[k] ?? tr(k) };
}
